import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ChevronDown, Loader2, MessageCircle, Send } from "lucide-react";

import {
  useGetLogbookMessagesQuery,
  useMarkLogbookMessagesReadMutation,
  useSendLogbookMessageMutation,
} from "../api/logbookApi";

/**
 * The candidate ↔ supervisor conversation, as a floating panel.
 *
 * Mounted on both sides of the same thread — the candidate's logbook page and
 * the supervisor's review page — because it is one conversation about one book.
 * The server decides which party the caller is, so this component does not need
 * to be told and cannot be told wrongly.
 *
 * There is no websocket on this host, so it polls: every 10 seconds while the
 * panel is open, every 60 while it is shut. That is enough for a conversation
 * that runs over days, and cheap enough to leave mounted — the API is on shared
 * hosting with a small worker pool, and a 2-second poll on every open logbook
 * page is exactly the sort of thing that exhausts it.
 */

function fmtTime(iso?: string | null) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) +
          " " +
          d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function LogbookChat({ logbookId }: { logbookId?: string | null }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading, isError, error } = useGetLogbookMessagesQuery(
    { logbookId: String(logbookId) },
    {
      skip: !logbookId,
      pollingInterval: open ? 10_000 : 60_000,
      refetchOnMountOrArgChange: true,
    },
  );

  const [send, { isLoading: sending }] = useSendLogbookMessageMutation();
  const [markRead] = useMarkLogbookMessagesReadMutation();

  const messages: any[] = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);
  const unread = Number(data?.unread_count ?? 0);
  const canSend = data?.can_send !== false;
  const counterpart = data?.counterpart as string | undefined;
  const role = data?.role as string | undefined;

  // Opening the panel is the moment the messages are actually read — not the
  // poll that fetched them.
  useEffect(() => {
    if (open && unread > 0 && logbookId) {
      markRead({ logbookId: String(logbookId) }).catch(() => {
        /* a failed read-receipt is not worth interrupting the conversation */
      });
    }
  }, [open, unread, logbookId, markRead]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: "end" });
  }, [open, messages.length]);

  if (!logbookId) return null;

  // The endpoint is missing until the migration runs on the server. Rather
  // than a permanently broken bubble, say nothing at all.
  const status = (error as any)?.status;
  if (isError && (status === 404 || status === 503 || status === 403)) return null;

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setErr(null);
    try {
      await send({ logbookId: String(logbookId), body }).unwrap();
      setDraft("");
    } catch (e: any) {
      setErr(e?.data?.message || e?.message || "Could not send. Please try again.");
    }
  };

  /* ── Collapsed ── */
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-indigo-500"
        title={counterpart ? `Message ${counterpart}` : "Messages"}
      >
        <MessageCircle className="h-5 w-5" />
        <span className="hidden sm:inline">
          {role === "supervisor" ? "Message candidate" : "Message supervisor"}
        </span>
        {unread > 0 ? (
          <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-bold text-indigo-700">
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>
    );
  }

  /* ── Open ── */
  return (
    <div className="fixed bottom-5 right-5 z-40 flex h-[32rem] max-h-[80vh] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-xl border bg-white shadow-2xl">
      <header className="flex items-center justify-between gap-2 bg-indigo-600 px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            {counterpart || (role === "supervisor" ? "Candidate" : "Supervisor")}
          </p>
          <p className="text-[11px] text-indigo-100">
            {role === "supervisor" ? "Your candidate" : "Your logbook supervisor"}
          </p>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="rounded-lg p-1.5 hover:bg-white/15"
          title="Minimise"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 space-y-2.5 overflow-y-auto bg-gray-50 px-3 py-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <p className="px-3 py-10 text-center text-[12px] text-gray-500">
            No messages yet.{" "}
            {role === "supervisor"
              ? "Send your candidate a note about their entries."
              : "Ask your supervisor about an entry, a training code, or your report."}
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                  m.mine
                    ? "rounded-br-sm bg-indigo-600 text-white"
                    : "rounded-bl-sm border bg-white text-gray-800"
                }`}
              >
                {/* The sender's name only on the other party's messages — you
                    know who you are. */}
                {!m.mine && m.sender ? (
                  <p className="mb-0.5 text-[10px] font-semibold text-gray-400">{m.sender}</p>
                ) : null}
                <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed">
                  {m.body}
                </p>
                <p
                  className={`mt-1 text-[10px] ${m.mine ? "text-indigo-200" : "text-gray-400"}`}
                >
                  {fmtTime(m.created_at)}
                  {m.mine && m.read_at ? " · Read" : ""}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <footer className="border-t bg-white px-3 py-2.5">
        {err ? (
          <p className="mb-2 flex items-start gap-1.5 text-[11px] text-rose-600">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {err}
          </p>
        ) : null}

        {canSend ? (
          <div className="flex items-end gap-2">
            <textarea
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line — the convention
                // everywhere else people type messages.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Write a message…"
              maxLength={4000}
              className="max-h-28 min-h-[2.5rem] flex-1 resize-y rounded-lg border px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <button
              onClick={submit}
              disabled={sending || !draft.trim()}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-40"
              title="Send"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        ) : (
          <p className="py-2 text-center text-[12px] text-gray-500">
            This logbook has no supervisor yet, so there is nobody to message.
          </p>
        )}
      </footer>
    </div>
  );
}
